import React, { useEffect, useState } from "react";
import { assignWorkerToFacility } from "../../../services/worker/assignWorkerFacility";

  export default function ReadyWorkforce({
  approvedWorkers = [],
  facilities = [],
  onViewWorker,
  onFacilityAssigned,
}) {
  const [selectedFacilities, setSelectedFacilities] = useState({});
  const [savingWorkerUid, setSavingWorkerUid] = useState("");
  const [message, setMessage] = useState("");
  const [localWorkers, setLocalWorkers] = useState(approvedWorkers);

useEffect(() => {
  setLocalWorkers(approvedWorkers);
}, [approvedWorkers]);

  async function handleAssignFacility(worker) {
    const workerUid = worker?.uid || worker?.id;
    const facilityId = selectedFacilities[workerUid];

    if (!workerUid) {
      setMessage("Worker UID is missing.");
      return;
    }

    if (!facilityId) {
      setMessage("Select a facility first.");
      return;
    }

    try {
      setSavingWorkerUid(workerUid);
      setMessage("");

      await assignWorkerToFacility(workerUid, facilityId);
      setLocalWorkers((prev) =>
  prev.map((w) =>
    (w.uid || w.id) === workerUid
      ? {
          ...w,
          facilityId,
          facilityIds: Array.from(
            new Set([...(w.facilityIds || []), facilityId])
          ),
        }
      : w
  )
);

      setMessage(
        `${worker.displayName || worker.fullName || worker.email || "Worker"} assigned to facility.`
      );

      setSelectedFacilities((prev) => ({
        ...prev,
        [workerUid]: "",
      }));

      onFacilityAssigned?.({
        worker,
        workerUid,
        facilityId,
      });
    } catch (err) {
      console.error("Assign worker facility error:", err);
      setMessage(err?.message || "Unable to assign worker to facility.");
    } finally {
      setSavingWorkerUid("");
    }
  }

  return (
    <div className="ao-ready-workforce"
      style={{
        padding: 16,
        borderRadius: 16,
        background: "#ffffff",
        border: "1px solid #e2e8f0",
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 950, marginBottom: 10 }}>
        Ready for Shifts
      </div>

      <div
        style={{
          color: "#64748b",
          fontSize: 12,
          fontWeight: 700,
          marginBottom: 12,
        }}
      >
        Workers below have completed onboarding, passed compliance review,
        and are ready at the tenant level. Facility assignment is a separate step. Assign facilities here or manage them
        from the Workforce tab.
      </div>

      <button
        type="button"
        onClick={() => onViewWorker?.(approvedWorkers[0])}
        disabled={localWorkers.length === 0}
        style={{
          marginBottom: 10,
          border: "1px solid #15803d",
          background: "#15803d",
          color: "#fff",
          borderRadius: 10,
          padding: "8px 12px",
          fontWeight: 900,
          cursor: localWorkers.length === 0 ? "not-allowed" : "pointer",
          opacity: localWorkers.length === 0 ? 0.6 : 1,
        }}
      >
        View Workforce
      </button>

      {message ? (
        <div
          style={{
            marginBottom: 10,
            padding: "9px 10px",
            borderRadius: 10,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1d4ed8",
            fontSize: 12,
            fontWeight: 850,
          }}
        >
          {message}
        </div>
      ) : null}

      { localWorkers.length === 0? (
        <div style={{ color: "#64748b", fontWeight: 800 }}>
          No approved workers in the pipeline yet.
        </div>
      ) : (
<div style={{ display: "grid", gap: 10 }}>
  {localWorkers.map((u) => {
    const workerUid = u.uid || u.id || "";
    const currentFacilityId = String(u.facilityId || "");
    const isSaving = savingWorkerUid === workerUid;

    const selectedFacilityId =
      selectedFacilities[workerUid] ?? currentFacilityId ?? "";

    const facilityChanged =
      selectedFacilityId &&
      selectedFacilityId !== currentFacilityId;

    return (
      <div
        key={workerUid || u.email}
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          padding: 12,
          background: "#f8fafc",
        }}
      >
        <div style={{ fontWeight: 950, color: "#0f172a" }}>
          {u.displayName ||
            u.fullName ||
            [u.firstName, u.lastName].filter(Boolean).join(" ") ||
            u.name ||
            u.email ||
            workerUid ||
            "Worker"}
        </div>

        <div
          style={{
            marginTop: 3,
            color: "#475569",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {u.email || workerUid}
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {currentFacilityId ? (
            <span style={{ color: "#15803d", fontWeight: 900 }}>
              ✓ {currentFacilityId}
            </span>
          ) : (
            <span style={{ color: "#64748b", fontWeight: 650 }}>
              Facility assignment pending
            </span>
          )}

          {" • "}

          {u.primaryRole ||
            u.position ||
            u.workerType ||
            u.roleType ||
            u.role ||
            u.platformRole ||
            "Worker"}

          {" • Ready for Shifts"}
        </div>

        <details className="ao-facility-disclosure"><summary>Manage facility assignment</summary><div
          style={{
            marginTop: 10,
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            gap: 8,
          }}
        >
          <select
            aria-label="Facility assignment"
            value={selectedFacilityId}
            onChange={(e) =>
              setSelectedFacilities((prev) => ({
                ...prev,
                [workerUid]: e.target.value,
              }))
            }
            disabled={isSaving}
            style={{
              minWidth: 0,
              height: 38,
              borderRadius: 10,
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              padding: "0 10px",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            <option value="">Select facility</option>

            {facilities.map((facility) => {
              const facilityId =
                facility.id ||
                facility.facilityId ||
                facility.uid ||
                "";

              const facilityName =
                facility.name ||
                facility.facilityName ||
                facility.displayName ||
                facilityId;

              return (
                <option key={facilityId} value={facilityId}>
                  {facilityName}
                </option>
              );
            })}
          </select>

          <button
            type="button"
            onClick={() => handleAssignFacility(u)}
            disabled={
              isSaving ||
              !workerUid ||
              !selectedFacilityId ||
              (currentFacilityId && !facilityChanged)
            }
            style={{
              border: "1px solid #2563eb",
              background: "#2563eb",
              color: "#ffffff",
              borderRadius: 10,
              padding: "8px 12px",
              fontSize: 12,
              fontWeight: 900,
              cursor:
                isSaving ||
                !selectedFacilityId ||
                (currentFacilityId && !facilityChanged)
                  ? "not-allowed"
                  : "pointer",
              opacity:
                isSaving ||
                !selectedFacilityId ||
                (currentFacilityId && !facilityChanged)
                  ? 0.55
                  : 1,
            }}
          >
            {isSaving
              ? "Saving..."
              : currentFacilityId
                ? "Change Facility"
                : "Assign Facility"}
          </button>
        </div></details>
      </div>
    );
  })}
</div>
      )}
    </div>
  );
}